/**
 * Canonical URL state for the public jobs board (PR11): search text,
 * employment type, hiring regions, and sort order live in the route's search
 * params so deep links and back/forward restore the exact view. Defaults are
 * omitted from the URL to keep links clean.
 */

export type JobsBrowseSort = "newest" | "oldest"

export type JobsBrowseSearchState = {
  q: string
  type: string
  regions: string
  sort: JobsBrowseSort
}

export const JOBS_BROWSE_DEFAULTS: JobsBrowseSearchState = {
  q: "",
  type: "all",
  regions: "",
  sort: "newest",
}

function asTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

/**
 * Tolerant parse: anything malformed falls back to the default view. Typed
 * Partial so links to the board don't have to spell out search params; the
 * screen overlays JOBS_BROWSE_DEFAULTS.
 */
export function parseJobsBrowseSearch(
  raw: Record<string, unknown>,
): Partial<JobsBrowseSearchState> {
  const sort: JobsBrowseSort = raw.sort === "oldest" ? "oldest" : "newest"
  const type = asTrimmedString(raw.type) || "all"
  return serializeJobsBrowseSearch({
    q: asTrimmedString(raw.q).slice(0, 200),
    type: type.slice(0, 50),
    regions: asTrimmedString(raw.regions).slice(0, 300),
    sort,
  })
}

/** Omit defaults so canonical URLs stay minimal and stable. */
export function serializeJobsBrowseSearch(
  state: JobsBrowseSearchState,
): Partial<JobsBrowseSearchState> {
  const out: Partial<JobsBrowseSearchState> = {}
  if (state.q !== JOBS_BROWSE_DEFAULTS.q) out.q = state.q
  if (state.type !== JOBS_BROWSE_DEFAULTS.type) out.type = state.type
  if (state.regions !== JOBS_BROWSE_DEFAULTS.regions) out.regions = state.regions
  if (state.sort !== JOBS_BROWSE_DEFAULTS.sort) out.sort = state.sort
  return out
}

export function jobsBrowseHasFilters(state: JobsBrowseSearchState): boolean {
  return (
    state.q.trim() !== "" ||
    state.type !== JOBS_BROWSE_DEFAULTS.type ||
    state.regions !== JOBS_BROWSE_DEFAULTS.regions
  )
}

/**
 * Guest board is a curated daily Top 10. Search, type, area, and sort
 * belong on the member board — URL params still parse so a later login
 * restores them.
 */
export function jobsBrowseShowFilters(topOnly: boolean | undefined): boolean {
  return topOnly !== true
}
